import { useState, useCallback, useEffect, useRef, useMemo } from "react";

export function useMathCaptcha() {
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, operator: "+" });
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const inputRef = useRef(null);

  const expectedResult = useMemo(() => {
    return captcha.operator === "+"
      ? captcha.num1 + captcha.num2
      : captcha.num1 - captcha.num2;
  }, [captcha]);

  const isValid = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return parseInt(trimmed, 10) === expectedResult;
  }, [value, expectedResult]);

  const generateCaptcha = useCallback(() => {
    const isAddition = Math.random() < 0.5;
    const operator = isAddition ? "+" : "-";
    let num1 = Math.floor(Math.random() * 20) + 1;
    let num2 = Math.floor(Math.random() * 20) + 1;

    if (operator === "-") {
      if (num1 < num2) {
        const temp = num1;
        num1 = num2;
        num2 = temp;
      }
    }

    setCaptcha({ num1, num2, operator });
    setValue("");
    setError("");
    setIsTouched(false);
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const refreshCaptcha = useCallback(() => {
    setIsRefreshing(true);
    generateCaptcha();
    setTimeout(() => {
      setIsRefreshing(false);
      inputRef.current?.focus();
    }, 300);
  }, [generateCaptcha]);

  const validateCaptcha = useCallback(() => {
    setIsTouched(true);
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Please answer the security question.");
      return false;
    }

    if (parseInt(trimmed, 10) !== expectedResult) {
      setError("Incorrect security answer. Please try again.");
      generateCaptcha();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return false;
    }

    setError("");
    return true;
  }, [value, expectedResult, generateCaptcha]);

  const handleChange = useCallback(
    (e) => {
      const rawVal = typeof e === "string" ? e : e?.target?.value ?? "";
      const numericVal = rawVal.replace(/\D/g, "");
      setValue(numericVal);
      setIsTouched(true);

      if (!numericVal.trim()) {
        setError("Please answer the security question.");
      } else if (parseInt(numericVal, 10) !== expectedResult) {
        setError("Incorrect security answer. Please try again.");
      } else {
        setError("");
      }
    },
    [expectedResult]
  );

  const question = `${captcha.num1} ${captcha.operator} ${captcha.num2} = ?`;

  return {
    question,
    value,
    setValue,
    error,
    setError,
    isValid,
    isTouched,
    isRefreshing,
    handleChange,
    refreshCaptcha,
    validateCaptcha,
    generateCaptcha,
    inputRef,
  };
}

export default useMathCaptcha;
